import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../../services/api';
import './GateDetailModal.css';

function GateDetailModal({
  isOpen,
  gateId,
  onClose,
  onNodeClick,
  onSynapseClick,
}) {
  const [gate, setGate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadGateDetails = useCallback(async () => {
    if (!gateId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await apiService.getGate(gateId);
      if (response.success) {
        setGate(response.gate);
      } else {
        setError(response.message || 'Failed to load gate details');
      }
    } catch (err) {
      console.error('Error loading gate details:', err);
      setError(err.response?.data?.message || 'Failed to load gate details');
    } finally {
      setLoading(false);
    }
  }, [gateId]);

  useEffect(() => {
    if (isOpen && gateId) {
      loadGateDetails();
    }
    if (!isOpen) {
      setGate(null);
      setError(null);
    }
  }, [isOpen, gateId, loadGateDetails]);

  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  const handleNodeNavigate = (nodeId, nodeName) => {
    if (onNodeClick) {
      onNodeClick({ id: nodeId, name: nodeName });
    }
  };

  const handleSynapseNavigate = (synapseId, edgeName) => {
    if (onSynapseClick) {
      onSynapseClick({ id: synapseId, synapseid: synapseId, edgeName });
    }
  };

  const renderConnectionCard = (conn, direction) => {
    const isInput = direction === 'input';
    const nodeId = isInput ? conn.sourceNodeId : conn.targetNodeId;
    const nodeName = isInput ? conn.sourceNodeName : conn.targetNodeName;
    const nodeType = isInput ? conn.sourceNodeType : conn.targetNodeType;

    return (
      <div key={conn.synapseId} className="gate-detail-connection-card">
        <div className="gate-detail-card-header">
          <button
            className="gate-detail-node-link"
            onClick={() => handleNodeNavigate(nodeId, nodeName)}
            title={`View node: ${nodeName}`}
          >
            {nodeName || 'Unknown Node'}
          </button>
          {nodeType && (
            <span className="gate-detail-node-type">{nodeType}</span>
          )}
        </div>
        <div className="gate-detail-card-meta">
          {conn.weight != null && (
            <span className="gate-detail-meta-item">
              <span className="gate-detail-meta-label">Weight:</span>
              <span className="gate-detail-meta-value">{Number(conn.weight).toFixed(3)}</span>
            </span>
          )}
          {conn.description && (
            <span className="gate-detail-meta-item gate-detail-meta-desc">
              <span className="gate-detail-meta-label">Description:</span>
              <span className="gate-detail-meta-value">{conn.description}</span>
            </span>
          )}
        </div>
        <div className="gate-detail-card-actions">
          <button
            className="gate-detail-action-link"
            onClick={() => handleSynapseNavigate(conn.synapseId, conn.description)}
          >
            View Synapse
          </button>
          <button
            className="gate-detail-action-link"
            onClick={() => handleNodeNavigate(nodeId, nodeName)}
          >
            View Node
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="gate-detail-overlay" onClick={handleOverlayClick}>
      <div className="gate-detail-modal">
        {/* Header */}
        <div className="gate-detail-header">
          <div className="gate-detail-header-info">
            <h2 className="gate-detail-title">
              {gate?.name || 'Gate Details'}
            </h2>
            {gate?.gateType && (
              <span className={`gate-detail-type-badge gate-detail-type-${gate.gateType.toLowerCase()}`}>
                {gate.gateType}
              </span>
            )}
          </div>
          <button className="gate-detail-close" onClick={onClose}>&times;</button>
        </div>

        {/* Body */}
        <div className="gate-detail-body">
          {loading && (
            <div className="gate-detail-loading">
              <div className="loading-spinner"></div>
              <p>Loading gate details...</p>
            </div>
          )}

          {error && (
            <div className="gate-detail-error">
              <p>{error}</p>
              <button className="gate-detail-retry" onClick={loadGateDetails}>Retry</button>
            </div>
          )}

          {gate && !loading && (
            <>
              {/* Summary */}
              <div className="gate-detail-summary">
                <div className="gate-detail-summary-item">
                  <span className="gate-detail-summary-count">{gate.inputSynapses?.length || 0}</span>
                  <span className="gate-detail-summary-label">Inputs</span>
                </div>
                <div className="gate-detail-summary-divider">
                  <span className="gate-detail-summary-arrow">&#8594;</span>
                  <span className={`gate-detail-summary-gate gate-detail-type-${gate.gateType?.toLowerCase()}`}>
                    {gate.gateType}
                  </span>
                  <span className="gate-detail-summary-arrow">&#8594;</span>
                </div>
                <div className="gate-detail-summary-item">
                  <span className="gate-detail-summary-count">{gate.outputSynapses?.length || 0}</span>
                  <span className="gate-detail-summary-label">Outputs</span>
                </div>
              </div>

              {/* Description & Condition */}
              {(gate.description || gate.condition) && (
                <div className="gate-detail-info-section">
                  {gate.description && (
                    <div className="gate-detail-info-row">
                      <span className="gate-detail-info-label">Description</span>
                      <span className="gate-detail-info-value">{gate.description}</span>
                    </div>
                  )}
                  {gate.condition && (
                    <div className="gate-detail-info-row">
                      <span className="gate-detail-info-label">Condition</span>
                      <code className="gate-detail-condition">{gate.condition}</code>
                    </div>
                  )}
                </div>
              )}

              {/* Inputs Section */}
              <div className="gate-detail-section">
                <h3 className="gate-detail-section-title">
                  <span className="gate-detail-section-icon">&#8592;</span>
                  Inputs
                  <span className="gate-detail-section-count">({gate.inputSynapses?.length || 0})</span>
                </h3>
                {gate.inputSynapses && gate.inputSynapses.length > 0 ? (
                  <div className="gate-detail-cards">
                    {gate.inputSynapses.map((conn) => renderConnectionCard(conn, 'input'))}
                  </div>
                ) : (
                  <div className="gate-detail-empty">No input connections</div>
                )}
              </div>

              {/* Outputs Section */}
              <div className="gate-detail-section">
                <h3 className="gate-detail-section-title">
                  <span className="gate-detail-section-icon">&#8594;</span>
                  Outputs
                  <span className="gate-detail-section-count">({gate.outputSynapses?.length || 0})</span>
                </h3>
                {gate.outputSynapses && gate.outputSynapses.length > 0 ? (
                  <div className="gate-detail-cards">
                    {gate.outputSynapses.map((conn) => renderConnectionCard(conn, 'output'))}
                  </div>
                ) : (
                  <div className="gate-detail-empty">No output connections</div>
                )}
              </div>

              {/* Metadata */}
              <div className="gate-detail-metadata">
                {gate.createDate && (
                  <span className="gate-detail-metadata-item">
                    Created: {new Date(gate.createDate).toLocaleDateString()}
                  </span>
                )}
                {gate.changeDate && (
                  <span className="gate-detail-metadata-item">
                    Modified: {new Date(gate.changeDate).toLocaleDateString()}
                  </span>
                )}
                {gate.id && (
                  <span className="gate-detail-metadata-item gate-detail-id">
                    ID: {gate.id}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GateDetailModal;
